import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { RecordSheet } from '@ordo/ui/record-sheet'
import { Skeleton } from '@ordo/ui/skeleton'
import { useInboundMessage, useInboundMessages, useRetryInboundMessage } from 'hooks/api/useInbound'
import { Paperclip, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import { inboundDeliveryStatus, inboundSenderLabel } from '../../lib/inbound'
import { InboundMessageContent } from './inbound-message'
import { InboundError } from './inbound-shared'

export function InboundMessages({ forwardingId }: { forwardingId: string }) {
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const messages = useInboundMessages({ forwardingId, query: query.trim() || undefined })
  const rows = messages.data?.filter((message) => message.forwardingId === forwardingId)

  return (
    <>
      {messages.loading && !messages.data ? (
        <InboundMessagesSkeleton />
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>Received messages</CardTitle>
              <Button variant="outline" size="sm" onClick={messages.refetch}>
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
            </div>
            <CardDescription>
              Latest 50 messages for this handler. Open a message to inspect its sender and forwarding status.
            </CardDescription>
            <Label htmlFor="inbound-search" className="sr-only">
              Search received messages
            </Label>
            <Input
              id="inbound-search"
              className="mt-3 max-w-md"
              placeholder="Search phone number or message ID"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </CardHeader>
          <CardContent>
            {messages.error ? <InboundError error={messages.error} /> : null}
            {rows?.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                {query.trim() ? 'No messages match this search.' : 'No messages received yet.'}
              </p>
            ) : null}
            <div className="divide-y">
              {(rows ?? []).map((message) => (
                <button
                  key={message.id}
                  type="button"
                  onClick={() => setOpenId(message.id)}
                  className="flex w-full min-w-0 flex-col gap-2 rounded-sm py-4 text-left outline-offset-4 hover:bg-muted/40 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <p className="break-words text-sm font-medium">{inboundSenderLabel(message)}</p>
                    <p className="break-words text-xs text-muted-foreground">
                      {message.fromPhone} → {message.toPhone}
                    </p>
                    <p className="line-clamp-2 break-words text-sm text-muted-foreground">
                      {message.body || 'Attachment message'}
                    </p>
                    {message.attachments.length ? (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Paperclip className="h-3 w-3" /> {message.attachments.length} attachment(s)
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-col sm:items-end">
                    <Badge variant={message.terminalAt ? 'destructive' : 'secondary'}>
                      {inboundDeliveryStatus(message)}
                    </Badge>
                    <time dateTime={message.receivedAt} className="text-xs text-muted-foreground">
                      {new Date(message.receivedAt).toLocaleString()}
                    </time>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <MessageSheet id={openId} onClose={() => setOpenId(null)} onRetried={messages.refetch} />
    </>
  )
}

function MessageSheet({ id, onClose, onRetried }: { id: string | null; onClose: () => void; onRetried: () => void }) {
  const message = useInboundMessage(id)
  const retry = useRetryInboundMessage()
  const [error, setError] = useState<unknown>(null)
  const row = message.data
  const retryMessage = async () => {
    if (!row) {
      return
    }
    setError(null)
    try {
      await retry.perform(row.id)
      message.refetch()
      onRetried()
    } catch (failure) {
      setError(failure)
    }
  }
  return (
    <RecordSheet
      open={id !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
          setError(null)
        }
      }}
      title="Received message"
      description={row && !message.loading ? inboundSenderLabel(row) : undefined}
      footerActions={
        row?.terminalAt && !message.loading && !row.filtered && !row.deliveredAt ? (
          <Button onClick={() => void retryMessage()} disabled={retry.loading}>
            <RefreshCw className="h-4 w-4" /> Retry forwarding
          </Button>
        ) : undefined
      }>
      {error || message.error ? <InboundError error={error || message.error} /> : null}
      {message.loading || (!row && !message.error) ? (
        <div className="space-y-5" role="status" aria-label="Loading message">
          <Skeleton className="h-6 w-32" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
          <Skeleton className="h-40 w-full" />
        </div>
      ) : row ? (
        <InboundMessageContent message={row} />
      ) : null}
    </RecordSheet>
  )
}

export function InboundMessagesSkeleton() {
  return (
    <Card role="status" aria-label="Loading messages">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-8 w-24" />
        </div>
        <Skeleton className="h-4 w-full max-w-xl" />
        <Skeleton className="mt-3 h-9 w-full max-w-md" />
      </CardHeader>
      <CardContent className="divide-y">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="flex flex-wrap gap-4 py-4">
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-40 max-w-full" />
              <Skeleton className="h-4 w-48 max-w-full" />
              <Skeleton className="h-4 w-full max-w-lg" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
